import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Zap, Heart } from 'lucide-react';

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

const PLACEHOLDER_IMG = "https://images.pexels.com/photos/8135269/pexels-photo-8135269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function ListingCard({ listing, promoted, showActions, isWatchlisted, onToggleWatchlist, showVerifiedBadge }) {
  const navigate = useNavigate();
  const image = listing.images?.length > 0 ? listing.images[0] : PLACEHOLDER_IMG;
  const isPromoted = promoted || (listing.promoted && listing.promoted_until && new Date(listing.promoted_until) > new Date());

  const handlePromote = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/promote/${listing.listing_id}`);
  };

  const handleWatchlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleWatchlist) onToggleWatchlist(listing.listing_id);
  };

  return (
    <Link
      to={`/listing/${listing.listing_id}`}
      className={`group block bg-white border border-zinc-200 rounded-lg overflow-hidden hover:border-zinc-300 hover:shadow-md hover:-translate-y-1 transition-all duration-200 ${isPromoted ? 'ring-1 ring-[#002FA7]/20' : ''}`}
      data-testid={`listing-card-${listing.listing_id}`}
    >
      <div className="relative bg-zinc-100 aspect-[4/3] overflow-hidden">
        <img
          src={image}
          alt={listing.title}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
        />
        {isPromoted && (
          <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#002FA7] text-white gap-0.5">
            <Zap className="w-3 h-3" /> Featured
          </span>
        )}
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/90 backdrop-blur text-zinc-700 capitalize">
          {listing.category}
        </span>
        {/* Watchlist Heart */}
        {onToggleWatchlist && (
          <button
            onClick={handleWatchlist}
            className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${isWatchlisted ? 'bg-red-500 text-white' : 'bg-white/80 backdrop-blur text-zinc-400 hover:text-red-500'}`}
            data-testid={`watchlist-btn-${listing.listing_id}`}
          >
            <Heart className={`w-4 h-4 ${isWatchlisted ? 'fill-current' : ''}`} />
          </button>
        )}
      </div>
      <div className="p-4">
        <p className="text-lg font-black text-zinc-900 mb-1" data-testid="listing-card-price">
          {listing.listing_type === 'lost_found' || listing.listing_type === 'announcement'
            ? <span className="text-sm font-bold text-zinc-500 uppercase">{listing.listing_type === 'lost_found' ? (listing.type_fields?.lost_or_found === 'found' ? 'Found' : 'Lost') : 'Announcement'}</span>
            : listing.price === 0
              ? <span className="text-emerald-600">Free</span>
              : <>{'$'}{listing.price?.toFixed(2)}{listing.listing_type === 'rental' && <span className="text-xs font-medium text-zinc-400">/mo</span>}</>
          }
        </p>
        <h3 className="text-sm font-semibold text-zinc-800 line-clamp-1 mb-2 group-hover:text-[#002FA7] transition-colors" data-testid="listing-card-title">
          {listing.title}
          {showVerifiedBadge && listing.seller_verified && (
            <span className="ml-1 inline-flex items-center text-[10px] text-emerald-600 font-bold">Verified</span>
          )}
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {listing.city}</span>
            <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {timeAgo(listing.created_at)}</span>
          </div>
          {showActions && !isPromoted && (
            <button
              onClick={handlePromote}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
              data-testid={`promote-btn-${listing.listing_id}`}
            >
              <Zap className="w-3 h-3" /> Promote
            </button>
          )}
          {showActions && isPromoted && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600" data-testid={`promoted-badge-${listing.listing_id}`}>
              <Zap className="w-3 h-3" /> Active
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
